import React, {
  ChangeEvent,
  FocusEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import debounce from 'lodash.debounce'
import {observer} from 'mobx-react-lite'
import {Box, BoxProps, Text} from '../../vanilla'
import {useGlobalStore} from '../../store/hooks/useStore'
import {Loqate} from '../../services/loqate'
import {Address, AddressDetails} from '../../types/loqate'
import {AddressSearchItem} from './address-search-item'
import {addressSearchDropdown} from './styles.css'
import {Field} from '../field'
import {Field as FieldType} from '../../types/forms'

interface LoqateFieldProps {
  field: FieldType
  showAsterisk?: boolean
  setFoundedAddressToForm: (address: AddressDetails, searchValue?: string) => void
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  onSearchValueChange?: (searchValue: string) => void
  usingHelpTooltip?: boolean
  helpTooltipText?: string
  inputGroupProps?: BoxProps<'input' | 'textarea'>
  children?: ReactNode
}

const DEFAULT_HELP_TEXT = 'Keep typing your address to display more results'

export const LoqateField = observer((props: LoqateFieldProps) => {
  const {
    field,
    showAsterisk = false,
    setFoundedAddressToForm,
    onSearchValueChange,
    onBlur,
    children,
    usingHelpTooltip = true,
    helpTooltipText = DEFAULT_HELP_TEXT,
    inputGroupProps = {},
  } = props

  const {loqateAPIKey} = useGlobalStore()
  const [loqateService] = useState(
    () => new Loqate({key: loqateAPIKey, limit: 7, countryCode: 'GBR'}),
  )

  const [helpTooltipOpen, setHelpTooltipOpen] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [addressSearchResult, setAddressSearchResult] = useState<Address[]>([])
  const [showAddresses, setShowAddresses] = useState<boolean>(false)

  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const debouncedSearch = useCallback(
    debounce(
      (inputText: string) =>
        inputText &&
        loqateService.searchAddresses(inputText).then((data) => {
          setAddressSearchResult(data.Items)
          setShowAddresses(true)
        }),
      400,
    ),
    [],
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e.target.value
    if (field.name === 'searchForAddress') {
      setSearchValue(fieldValue)
    }

    if (fieldValue) {
      debouncedSearch(fieldValue)
    } else {
      // Hide if search box is empty
      setShowAddresses(false)
    }

    if (onSearchValueChange && typeof onSearchValueChange === 'function') {
      onSearchValueChange(fieldValue)
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // If the customer clicks off and clicks back, show the search result
    if (e.currentTarget.value.length >= 1 && !!addressSearchResult.length) {
      setShowAddresses(true)
    }
  }

  const handleSelectAddress = async (address: Address) => {
    if (address.Type !== 'Address') {
      setSearchValue(address.Text)
      const addresses = await loqateService.searchAddresses(address.Text, address.Id)
      setAddressSearchResult(addresses.Items)
    } else {
      const details = await loqateService.getAddressDetails(address.Id)

      if (details && typeof setFoundedAddressToForm === 'function') {
        setFoundedAddressToForm(details, searchValue)
      }

      setShowAddresses(false)
    }
  }

  useEffect(() => {
    if (addressSearchResult.length) {
      setHelpTooltipOpen(true)
    }
  }, [addressSearchResult])

  useEffect(() => {
    const onClick = ({target}: any) => {
      !dropdownRef?.current?.contains(target) && setShowAddresses(false)
    }

    document.addEventListener('click', onClick, {capture: true})

    return () => document.removeEventListener('click', onClick, {capture: true})
  }, [])

  return (
    <Box position="relative" {...inputGroupProps}>
      <Field
        {...(field || {})}
        inputProps={({onBlur: fieldOnBlur}) => ({
          id: `search-for-address-${field.name}`,
          'data-test-selector': `search-for-address-${field.name}`,
          showAsterisk,
          onChange: handleChange,
          onClick: handleClick,
          onBlur: (e: FocusEvent<HTMLInputElement>) => {
            fieldOnBlur?.()

            if (typeof onBlur === 'function') {
              onBlur(e)
            }
          },
          // this prevents the browser address autofill from popping up and covering the suggested results
          autoComplete: 'chrome-off',
        })}
      />
      {usingHelpTooltip && (
        <HelpTooltip
          isOpen={helpTooltipOpen}
          setIsOpen={setHelpTooltipOpen}
          text={helpTooltipText}
        />
      )}
      {children}
      {showAddresses && (
        <Box
          position="absolute"
          border="1px"
          borderColor="gray200"
          zIndex="docked"
          overflowY="auto"
          ref={dropdownRef}
          className={addressSearchDropdown}
        >
          {addressSearchResult.map((address) => (
            <AddressSearchItem
              key={address.Id}
              address={address}
              onSelectAddress={handleSelectAddress}
            />
          ))}
        </Box>
      )}
    </Box>
  )
})

interface HelpTooltipProps {
  isOpen: boolean
  setIsOpen: (state: boolean) => void
  text: string
}

const HelpTooltip = ({isOpen, setIsOpen, text}: HelpTooltipProps) => {
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsOpen(false)
      }, 2000)
    }
  }, [isOpen])

  if (!text) return null

  return (
    <Box
      bg="gray25"
      position="absolute"
      zIndex="tooltip"
      paddingY="8px"
      paddingX="12px"
      opacity={isOpen ? '1' : '0'}
      style={{
        transition: 'opacity 500ms ease',
        top: -10,
        boxShadow: '0 2px 5px 0 rgb(0 0 0 / 26%)',
        transform: 'translateY(-100%)',
        borderRadius: '2px',
        visibility: isOpen ? 'visible' : 'hidden',
      }}
    >
      <Text variant="unstyled" lineHeight="none" fontSize="xs" style={{fontFamily: 'arial'}}>
        {text}
      </Text>
    </Box>
  )
}
